import { MenuItem } from '@lokalise/louis'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDeactivateProfile } from '../../../hooks/useDeactivateProfile'
import type { UserCreatedProfile } from '../../../types/tag'
import { getNewEvaluationsUrl } from '../../../utils/url'
import { DeleteUserCreatedProfileModal } from '../../DeleteUserCreatedProfileModal/DeleteUserCreatedProfileModal'
import { UserCreatedProfileModal } from '../../UserCreatedProfileModal/UserCreatedProfileModal'
import { CardContainer } from '../components/CardContainer/CardContainer'
import { CardModel } from '../components/CardModel/CardModel'
import { CardTitle } from '../components/CardTitle/CardTitle'
import { EditMenu } from '../components/EditMenu/EditMenu'
import { EvaluationsButton } from '../components/EvaluationsButton/EvaluationsButton'
import { ProjectsList } from '../components/ProjectsList/ProjectsList'
import { CustomTag, ProjectTag } from '../components/ProjectTag/ProjectTag'
import styles from './ProfileCard.module.css'

type ProfileCardProps = {
  profile: UserCreatedProfile
  enableEvaluations?: boolean
  evaluationsLimit?: number
  evaluationsRenewalDate?: string
  isAtEvaluationsLimit?: boolean
  isLoadingEvaluations?: boolean
  showEdit?: boolean
}

export const ProfileCard = ({
  profile,
  enableEvaluations,
  evaluationsLimit,
  evaluationsRenewalDate,
  isAtEvaluationsLimit,
  isLoadingEvaluations,
  showEdit,
}: ProfileCardProps) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const navigate = useNavigate()
  const { deactivate, isPending: isDeactivating } = useDeactivateProfile({ profile })

  const hasEvaluationReport = (profile?.evaluations?.count || 0) > 0
  const isEvaluationInProgress = profile.evaluations?.status === 'in-progress'

  const newEvaluationsPageUrl = getNewEvaluationsUrl(profile.id)

  return (
    <>
      <CardContainer>
        <div className="flex justify-between">
          <div className="flex-col gap-3">
            <div className="flex gap-2">
              <CustomTag />
              <ProjectTag type={isDeactivating ? 'updating' : profile.status} />
              <CardModel integration={profile.config.integration} />
            </div>
            <CardTitle title={profile.name} />
          </div>
          <div className={`${styles.editMenuContainer} flex gap-2 items-center`}>
            {enableEvaluations && (
              <EvaluationsButton
                profileId={profile.id}
                isInProgress={isEvaluationInProgress}
                hasEvaluations={hasEvaluationReport}
                evaluationsLimit={evaluationsLimit}
                evaluationsRenewalDate={evaluationsRenewalDate}
                isAtEvaluationsLimit={isAtEvaluationsLimit}
                isLoadingEvaluations={isLoadingEvaluations}
              />
            )}
            {showEdit && (
              <EditMenu>
                <MenuItem onClick={() => setIsEditModalOpen(true)}>Edit</MenuItem>
                {enableEvaluations && hasEvaluationReport && (
                  <MenuItem
                    onClick={() => navigate(newEvaluationsPageUrl)}
                    disabled={isEvaluationInProgress || isAtEvaluationsLimit}
                  >
                    Reevaluate
                  </MenuItem>
                )}
                {profile.projects.length > 0 && (
                  <MenuItem onClick={() => deactivate({ profileId: profile.id })}>
                    Deactivate
                  </MenuItem>
                )}
                <MenuItem
                  className={styles.deleteMenuItem}
                  onClick={() => setIsDeleteModalOpen(true)}
                >
                  Delete
                </MenuItem>
              </EditMenu>
            )}
          </div>
        </div>
        <ProjectsList projects={profile.projects} />
      </CardContainer>
      {isEditModalOpen && (
        <UserCreatedProfileModal onClose={() => setIsEditModalOpen(false)} profile={profile} />
      )}
      {isDeleteModalOpen && (
        <DeleteUserCreatedProfileModal
          profile={profile}
          onClose={() => setIsDeleteModalOpen(false)}
        />
      )}
    </>
  )
}
